import { Document, OntologyStatusEntry, StatusEnum } from "@/types/document";

// The ontologies a document's generation status is shown for. Design is derived
// from functional, so design views read the functional entry.
export type DocOntology = "functional" | "architecture";

// Stale timeout for inprogress status (1 hour in milliseconds)
export const STALE_INPROGRESS_TIMEOUT_MS = 1 * 60 * 60 * 1000;

// Check if a timestamp is stale (more than 1 hour old)
export const isStaleTimestamp = (timestamp?: string | number): boolean => {
  if (!timestamp) return false;
  const time = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
  const now = Date.now();
  return (now - time) > STALE_INPROGRESS_TIMEOUT_MS;
};

export interface MappedOntologyStatus {
  status: StatusEnum;
  isTimedOut: boolean;
  // A cancelled job shows as StatusEnum.error (so the card offers Retry); this
  // lets the badge say "Cancelled" instead of "Error".
  isCancelled: boolean;
}

// Maps a document's per-ontology generation entry (`doc.ontologyStatus[x]`, from
// the run tables) onto the StatusEnum the cards and counts use. Mirrors how a
// run row is read elsewhere: a V2 ("ticket") run covers several ontologies, so
// one job can finish while the run is still working — stay "In Progress" until
// the run reaches review; a V2 run that failed after its job finished (the
// write) is still a failure. A run waiting for review reads as done.
export const mapOntologyStatusEntry = (
  entry: OntologyStatusEntry | null | undefined,
): MappedOntologyStatus => {
  if (!entry) return { status: StatusEnum.none, isTimedOut: false, isCancelled: false };

  const inProgress = (): MappedOntologyStatus => ({
    status: StatusEnum.inprogress,
    isTimedOut: isStaleTimestamp(entry.updatedAt),
    isCancelled: false,
  });

  if (entry.agent === "ticket" && (entry.runStatus === "running" || entry.runStatus === "pending")) {
    return inProgress();
  }
  if (entry.agent === "ticket" && entry.runStatus === "error") {
    return { status: StatusEnum.error, isTimedOut: false, isCancelled: false };
  }
  switch (entry.status) {
    case "pending":
    case "running":
      return inProgress();
    case "done":
      return { status: StatusEnum.done, isTimedOut: false, isCancelled: false };
    case "cancelled":
      return { status: StatusEnum.error, isTimedOut: false, isCancelled: true };
    case "error":
      return { status: StatusEnum.error, isTimedOut: false, isCancelled: false };
    default:
      return { status: StatusEnum.none, isTimedOut: false, isCancelled: false };
  }
};

// A document's generation status for one ontology.
export const getDocumentOntologyStatus = (
  doc: Pick<Document, "ontologyStatus"> | null | undefined,
  ontology: DocOntology,
): MappedOntologyStatus => mapOntologyStatusEntry(doc?.ontologyStatus?.[ontology]);

